<template>
  <sign-in
    class="step-up"
    :title="$t('stepUp.title')"
  >
    <sign-in-content>
      <div class="_explanation">
        {{ $t('stepUp.explanation', { account: accountName }) }}
      </div>
      <m-card
        padding-xs
        flat
        bordered
        class="_context"
      >
        <div class="_context-inner">
          <m-avatar
            :src="accountImage"
            :text="accountName"
            :amount-chars="1"
            :size="36"
          />
          <div class="_text">
            <div class="_account">
              {{ accountName }}
            </div>
            <div class="_email">
              {{ $t('stepUp.loggedInAs', { email: me?.email ?? '' }) }}
            </div>
          </div>
        </div>
      </m-card>
      <sso-buttons
        class="_sso"
        :google="offers(authMethod.ssoGoogle)"
        :microsoft="offers(authMethod.ssoMicrosoft)"
        :saml="offers(authMethod.saml)"
        :loading="loading"
        @google="viaOAuth(oAuthProvider.google, authMethod.ssoGoogle)"
        @microsoft="viaOAuth(oAuthProvider.microsoft, authMethod.ssoMicrosoft)"
        @saml="viaSaml"
      />
      <template v-if="offers(authMethod.password)">
        <div
          v-if="offersSso"
          class="_or"
        >
          {{ $t('general.or') }}
        </div>
        <password-login
          class="_password"
          hide-email
          hide-register-link
          :initial-email="me?.email ?? ''"
          :loading="loading === authMethod.password"
          @submit="viaPassword"
        />
      </template>
      <m-divider
        v-if="methods.length > 0"
        small
      />
      <div class="_escape">
        <div
          v-if="showFallbackAccounts"
          class="_switch"
        >
          <div class="_hint">
            {{ $t('stepUp.switchInstead') }}
          </div>
          <div class="_accounts">
            <sign-in-account-row
              v-for="account in fallbackAccounts"
              :key="account.uid"
              :name="account.companyName || $t('accountPicker.untitledAccount')"
              :image="account.companyImage?.getURL || ''"
              :locked="account.accessible === false"
              @select="abandonTo(account)"
            />
          </div>
        </div>
        <div class="_logout">
          <m-link
            inherit-color
            underlined
            @click="logout"
          >
            {{ $t('navigation.logout') }}
          </m-link>
        </div>
      </div>
    </sign-in-content>
  </sign-in>
</template>

<script setup>
import PasswordLogin from '@/components/PasswordLogin.vue';
import SignIn from '@/components/SignIn.vue';
import SignInAccountRow from '@/components/SignInAccountRow.vue';
import SignInContent from '@/components/SignInContent.vue';
import SsoButtons from '@/components/SsoButtons.vue';
import useAccountSwitch from '@/composables/account-switch';
import useMe from '@/composables/me';
import useSnackBar from '@/composables/snackbar';
import { EventBus } from '@/lib/event-bus';
import { authMethod, oAuthProvider } from 'shared/constants.json';
import { computed, onMounted, ref, watch } from 'vue';
import { doGetPublicAccountData } from '@/api';
import { doLoginUser } from '@/api/sentinel';
import { encodeURLValues } from '@/lib/url';
import { initOAuthLogin } from '@/lib/oauth-redirect';
import { initSamlLogin } from '@/lib/saml';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const emit = defineEmits(['session-established']);

const store = useStore();
const snackbar = useSnackBar();
const { t, te } = useI18n();
const { me, refreshMe, currentTenant, reachableAccounts } = useMe();
const { switchAccount } = useAccountSwitch();

const stepUp = computed(() => store.state.stepUp);
const publicData = ref(null);
const loading = ref(null);

onMounted(() => {
  if (!stepUp.value?.slug) {
    return;
  }
  doGetPublicAccountData(stepUp.value.slug).then((response) => {
    if (response?.status !== 200) {
      return;
    }
    publicData.value = response.data;
  });
});

const accountName = computed(() => publicData.value?.companyName
  || reachableAccounts.value.find((account) => account.uid === stepUp.value?.accountId)?.companyName
  || t('accountPicker.untitledAccount'));

const accountImage = computed(() => publicData.value?.companyImage?.getURL
  || reachableAccounts.value.find((account) => account.uid === stepUp.value?.accountId)?.companyImage?.getURL
  || '');

const methods = computed(() => {
  const acceptable = stepUp.value?.acceptable ?? [];
  if (acceptable.length > 0) {
    return acceptable.map((requirement) => requirement.method);
  }

  if (publicData.value === null) {
    return [];
  }
  const derived = [];
  if (!publicData.value.restrictPasswordLogin) {
    derived.push(authMethod.password);
  }
  if (!publicData.value.restrictGoogleLogin) {
    derived.push(authMethod.ssoGoogle);
  }
  if (!publicData.value.restrictMicrosoftLogin) {
    derived.push(authMethod.ssoMicrosoft);
  }
  if (publicData.value.usesSaml && publicData.value.samlConfigured) {
    derived.push(authMethod.saml);
  }
  return derived;
});

const offers = (method) => methods.value.includes(method);

const offersSso = computed(() => [authMethod.ssoGoogle, authMethod.ssoMicrosoft, authMethod.saml].some(offers));

const fallbackAccounts = computed(() => reachableAccounts.value
  .filter((account) => account.uid !== stepUp.value?.accountId));

const showFallbackAccounts = computed(() => stepUp.value?.sameAccountOnly !== true
  && fallbackAccounts.value.length > 0);

const route = useRoute();
watch(() => route.fullPath, () => {
  if (currentTenant.value === null || currentTenant.value.accountId === stepUp.value?.accountId) {
    return;
  }
  store.commit('STEP_UP_RESOLVED');
});

const resume = () => refreshMe().then((refreshed) => {
  const target = (refreshed?.users ?? [])
    .map((user) => user.account)
    .find((account) => account?.uid === stepUp.value?.accountId);
  if (!target || target.accessible === false) {
    snackbar.error(t('stepUp.stillLocked'));
    return;
  }

  const resolved = stepUp.value;
  store.commit('STEP_UP_RESOLVED');

  if (currentTenant.value !== null && currentTenant.value.accountId !== resolved.accountId) {
    switchAccount(target);
    return;
  }

  emit('session-established');
});

const onPopupEvent = (event) => {
  const { error } = event.data;
  if (error) {
    const key = [`stepUp.popupError.${error}`, `login.samlLoginStatus.${error}`].find(te);
    snackbar.error(key === undefined ? t('error.default') : t(key));
    return Promise.resolve();
  }
  return resume();
};

const popup = (openPopup, method) => {
  loading.value = method;
  openPopup()
    .then(onPopupEvent)
    .catch(() => {})
    .finally(() => {
      loading.value = null;
    });
};

const viaOAuth = (provider, method) => popup(
  () => initOAuthLogin(`/#/oauth-redirect?${encodeURLValues({ action: 'login', provider, reauth: '1' })}`),
  method,
);

const viaSaml = () => popup(
  () => initSamlLogin({ email: me.value?.email ?? '', slug: stepUp.value?.slug, reauth: true }),
  authMethod.saml,
);

const viaPassword = ({ email, password }) => {
  loading.value = authMethod.password;
  doLoginUser({ email, password })
    .then((response) => {
      if (response?.status !== 200) {
        snackbar.error(t('login.emailPasswordCombinationWrong'));
        return Promise.resolve();
      }
      return resume();
    })
    .finally(() => {
      loading.value = null;
    });
};

const abandonTo = (account) => {
  store.commit('STEP_UP_RESOLVED');
  switchAccount(account);
};

const logout = () => {
  EventBus.$emit('logout');
};
</script>

<style scoped lang="scss" type="text/scss">
  .step-up {
    ._explanation {
      margin-bottom: 2rem;
      color: $font-color-secondary;
      text-align: center;
    }

    ._context {
      margin-bottom: 2rem;

      ._context-inner {
        display: flex;
        align-items: center;
        gap: 1rem;

        ._text {
          flex: 1;
          overflow: hidden;

          ._account {
            font-weight: $font-weight-bold;
          }

          ._email {
            overflow: hidden;
            font-size: $font-size-3;
            color: $font-color-secondary;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }

    ._or {
      margin: .8rem 0;
      font-size: $font-size-3;
      color: $font-color-secondary;
      text-align: center;
    }

    ._escape {
      ._switch {
        margin-bottom: 1rem;

        ._hint {
          margin-bottom: .8rem;
          font-size: $font-size-3;
          color: $font-color-secondary;
        }

        ._accounts {
          display: flex;
          flex-direction: column;
          gap: .8rem;
        }
      }

      ._logout {
        font-size: $font-size-3;
        color: $font-color-secondary;
        text-align: center;
      }
    }
  }
</style>
